import { useState, useEffect } from "react";
import { t } from "@/lib/i18n";
import { Copy, Plugs, ArrowSquareOut } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

export const McpPanel = () => {
  const [keys, setKeys] = useState([]);
  const [sel, setSel] = useState("");
  const [recent, setRecent] = useState([]);
  const [tools, setTools] = useState([]);
  const [showTools, setShowTools] = useState(false);
  useEffect(() => {
    api.get("/apikeys").then((r) => {
      const mk = r.data.filter((k) => (k.scopes || ["mcp"]).includes("mcp") && k.active !== false);
      setKeys(mk);
      if (mk[0]) setSel(mk[0].key);
    }).catch(() => {});
    api.get("/apikeys/audit/recent?scope=mcp").then((r) => setRecent(r.data)).catch(() => {});
    api.get("/mcp").then((r) => setTools(r.data.tools || [])).catch(() => {});
  }, []);

  const url = `${process.env.REACT_APP_BACKEND_URL}/api/mcp`;
  const key = sel || "<YOUR_API_KEY>";
  const snippets = {
    claude: JSON.stringify({ mcpServers: { "agentic-inbox": { command: "npx", args: ["mcp-remote", url, "--header", `X-API-Key: ${key}`] } } }, null, 2),
    cursor: JSON.stringify({ mcpServers: { "agentic-inbox": { url, headers: { "X-API-Key": key } } } }, null, 2),
    chatgpt: `MCP Server URL: ${url}\nAuthentication header: X-API-Key: ${key}`,
  };
  const copy = (kind) => { navigator.clipboard?.writeText(snippets[kind]); toast.success(t("mcp.config_copied", "Config copied to clipboard")); };

  return (
    <div className="flex-1 overflow-y-auto scroll-thin p-4 text-sm" data-testid="mcp-panel">
      <p className="font-semibold text-gray-800 mb-1 flex items-center gap-1.5"><Plugs size={15} className="text-blue-600" /> {t("mcp.title", "MCP Server")}</p>
      <p className="text-xs text-gray-500 mb-3">{t("mcp.desc", "Connect Claude Desktop, ChatGPT or Cursor to your full inbox — search, stats, drafts, rules, calendar and more.")}</p>

      <label className="text-[11px] font-medium text-gray-500 block mb-1">{t("mcp.server_url", "Server URL")}</label>
      <div className="flex items-center gap-1.5 mb-3">
        <code data-testid="mcp-server-url" className="flex-1 text-[11px] bg-white border border-gray-200 rounded px-2 py-1.5 break-all">{url}</code>
        <button data-testid="mcp-copy-url" onClick={() => { navigator.clipboard?.writeText(url); toast.success(t("mcp.url_copied", "URL copied")); }}
          className="text-gray-400 hover:text-gray-700 p-1"><Copy size={14} /></button>
      </div>

      <label className="text-[11px] font-medium text-gray-500 block mb-1">{t("mcp.key_label", "API key (needs the MCP scope)")}</label>
      {keys.length > 0 ? (
        <select data-testid="mcp-key-select" value={sel} onChange={(e) => setSel(e.target.value)}
          className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs bg-white mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500">
          {keys.map((k) => <option key={k.id} value={k.key}>{k.name} — {k.key.slice(0, 12)}…</option>)}
        </select>
      ) : (
        <p data-testid="mcp-no-keys" className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mb-3">
          {t("mcp.no_keys", "No MCP-scoped key yet.")} <Link to="/settings" className="font-semibold underline">{t("mcp.create_in_settings", "Create one in Settings")} <ArrowSquareOut size={11} className="inline" /></Link>
        </p>
      )}

      <label className="text-[11px] font-medium text-gray-500 block mb-1.5">{t("mcp.copy_for", "Copy config for")}</label>
      <div className="flex gap-1.5 mb-3">
        <button data-testid="mcp-copy-claude" onClick={() => copy("claude")}
          className="flex-1 border border-gray-300 hover:bg-white text-xs font-semibold rounded-md py-1.5 transition-colors">Claude</button>
        <button data-testid="mcp-copy-chatgpt" onClick={() => copy("chatgpt")}
          className="flex-1 border border-gray-300 hover:bg-white text-xs font-semibold rounded-md py-1.5 transition-colors">ChatGPT</button>
        <button data-testid="mcp-copy-cursor" onClick={() => copy("cursor")}
          className="flex-1 border border-gray-300 hover:bg-white text-xs font-semibold rounded-md py-1.5 transition-colors">Cursor</button>
      </div>

      <button data-testid="mcp-tools-toggle" onClick={() => setShowTools(!showTools)}
        className="w-full flex items-center justify-between text-[11px] font-medium text-gray-500 hover:text-gray-800 border border-gray-200 rounded-md px-2 py-1.5 mb-2 transition-colors">
        <span>{t("mcp.tools", "Available tools")} ({tools.length})</span>
        <span>{showTools ? "▾" : "▸"}</span>
      </button>
      {showTools && (
        <div data-testid="mcp-tools-list" className="flex flex-wrap gap-1 mb-3">
          {tools.map((name) => (
            <span key={name} data-testid={`mcp-tool-${name}`} className="text-[10px] font-mono bg-white border border-gray-200 rounded px-1.5 py-0.5 text-gray-600">{name}</span>
          ))}
        </div>
      )}

      <p className="text-[11px] font-medium text-gray-500 mb-1.5">{t("mcp.recent", "Recent MCP calls")}</p>
      <div data-testid="mcp-recent-calls" className="space-y-1">
        {recent.length === 0 && <p className="text-xs text-gray-400">{t("mcp.no_calls", "No MCP calls yet — connect a client and ask it about your inbox.")}</p>}
        {recent.map((r, i) => (
          <div key={i} className="flex items-center gap-1.5 text-[11px] text-gray-600 bg-white border border-gray-100 rounded px-2 py-1">
            <span className={r.ok ? "text-green-600" : "text-red-500"}>{r.ok ? "✓" : "✗"}</span>
            <span className="font-mono truncate">{r.tool || r.scope}</span>
            {!r.ok && <span className="text-red-400 truncate">{r.reason}</span>}
            <span className="ml-auto text-gray-400 shrink-0">{(r.ts || "").slice(11, 16)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
